import React from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import { formatCurrency } from 'utils/CommonFunction';
import PropTypes from 'prop-types';

export const CustomTooltipForGrossRevenue = ({
  active,
  payload,
  currency,
  showAverage,
  grossRevenueAvg,
  globalMerchantColors,
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <div
        className="custom-tooltip"
        style={{
          background: '#fff',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '300px',
        }}
      >
        {/* Average */}
        {showAverage && (
          <Flex alignItems="center" mb="2">
            <Box w="10px" h="10px" bg="red" borderRadius="50%" mr="2" />
            <Text fontSize="sm">
              Average: {formatCurrency(grossRevenueAvg, currency)}
            </Text>
          </Flex>
        )}

        {/* Conditional content based on merchant data */}
        {data.merchants &&
        Array.isArray(data.merchants) &&
        data.merchants.length > 0 ? (
          // Show merchant details when merchants exist
          <>
            <Text fontWeight="bold" mb="2" fontSize="sm">
              {data.time}
            </Text>
            {(() => {
              // Filter out merchants with zero values and sort by total (descending)
              const activeMerchants = data.merchants
                .filter((merchant) => Number(merchant.total) > 0)
                .sort((a, b) => Number(b.total) - Number(a.total));

              const maxDisplay = 8; // Show max 8 merchants
              const displayMerchants = activeMerchants.slice(0, maxDisplay);
              const remainingCount = activeMerchants.length - maxDisplay;

              return (
                <>
                  {/* Scrollable container for merchant list */}
                  <Box maxH="200px" overflowY="auto" mb="2">
                    {displayMerchants.map((merchant) => {
                      const color =
                        globalMerchantColors[merchant.merchantId] || '#666';

                      return (
                        <Flex
                          key={merchant.merchantId}
                          alignItems="center"
                          mb="1"
                        >
                          <Box
                            w="8px"
                            h="8px"
                            bg={color}
                            borderRadius="50%"
                            mr="2"
                          />
                          <Text fontSize="xs" fontWeight="medium" flex="1">
                            {merchant.name.length > 15
                              ? `${merchant.name.substring(0, 15)}...`
                              : merchant.name}
                            :{' '}
                            <Text as="span" fontWeight="bold">
                              {formatCurrency(merchant.total, currency)}
                            </Text>
                          </Text>
                        </Flex>
                      );
                    })}
                  </Box>

                  {/* Summary for remaining merchants */}
                  {remainingCount > 0 && (
                    <Text fontSize="xs" color="gray.500" textAlign="center">
                      +{remainingCount} more merchants
                    </Text>
                  )}

                  {/* Total summary */}
                  <hr style={{ margin: '8px 0', borderColor: '#e2e8f0' }} />
                  <Flex alignItems="center" justify="space-between">
                    <Text fontSize="sm" fontWeight="bold">
                      Total Revenue:
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {formatCurrency(data.total, currency)}
                    </Text>
                  </Flex>
                </>
              );
            })()}
          </>
        ) : (
          // Show original total and previous data when no merchants
          <>
            <Flex alignItems="center" mb="1">
              <Box w="10px" h="10px" bg="#000000" borderRadius="50%" mr="2" />
              <Text fontSize="sm">
                {data.time}: {formatCurrency(data.total, currency)}
              </Text>
            </Flex>
            <Flex alignItems="center" mb="1">
              <Box w="10px" h="10px" bg="#D3D3D3" borderRadius="50%" mr="2" />
              <Text fontSize="sm">
                {data.prevTime}: {formatCurrency(data.prevTotal, currency)}
              </Text>
            </Flex>
          </>
        )}
      </div>
    );
  }
  return null;
};

CustomTooltipForGrossRevenue.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  currency: PropTypes.string,
  showAverage: PropTypes.bool,
  grossRevenueAvg: PropTypes.number,
  globalMerchantColors: PropTypes.object,
};

export const CustomTooltipForPowerbankRented = ({
  active,
  payload,
  powerbankShowAvg,
  grossRevenueCountAvg,
  globalMerchantColors,
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        className="custom-tooltip"
        style={{
          background: '#fff',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '300px',
        }}
      >
        {/* Average */}
        {powerbankShowAvg && (
          <Flex alignItems="center" mb="2">
            <Box w="10px" h="10px" bg="red" borderRadius="50%" mr="2" />
            <Text fontSize="sm">Average: {grossRevenueCountAvg ?? 0}</Text>
          </Flex>
        )}

        {/* Conditional content based on merchant data */}
        {data.merchants &&
        Array.isArray(data.merchants) &&
        data.merchants.length > 0 ? (
          // Show merchant details when merchants exist
          <>
            <Text fontWeight="bold" mb="2" fontSize="sm">
              {data.time}
            </Text>
            {(() => {
              // Filter out merchants with zero values and sort by total (descending)
              const activeMerchants = data.merchants
                .filter((merchant) => Number(merchant.total) > 0)
                .sort((a, b) => Number(b.total) - Number(a.total));

              const maxDisplay = 8; // Show max 8 merchants
              const displayMerchants = activeMerchants.slice(0, maxDisplay);
              const remainingCount = activeMerchants.length - maxDisplay;

              return (
                <>
                  {/* Scrollable container for merchant list */}
                  <Box maxH="200px" overflowY="auto" mb="2">
                    {displayMerchants.map((merchant) => {
                      const color =
                        globalMerchantColors[merchant.merchantId] || '#666';

                      return (
                        <Flex
                          key={merchant.merchantId}
                          alignItems="center"
                          mb="1"
                        >
                          <Box
                            w="8px"
                            h="8px"
                            bg={color}
                            borderRadius="50%"
                            mr="2"
                          />
                          <Text fontSize="xs" fontWeight="medium" flex="1">
                            {merchant.name.length > 15
                              ? `${merchant.name.substring(0, 15)}...`
                              : merchant.name}
                            :{' '}
                            <Text as="span" fontWeight="bold">
                              {Number(merchant.total)}
                            </Text>
                          </Text>
                        </Flex>
                      );
                    })}
                  </Box>

                  {/* Summary for remaining merchants */}
                  {remainingCount > 0 && (
                    <Text fontSize="xs" color="gray.500" textAlign="center">
                      +{remainingCount} more merchants
                    </Text>
                  )}

                  {/* Total summary */}
                  <hr style={{ margin: '8px 0', borderColor: '#e2e8f0' }} />
                  <Flex alignItems="center" justify="space-between">
                    <Text fontSize="sm" fontWeight="bold">
                      Total Powerbanks:
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {data.total}
                    </Text>
                  </Flex>
                </>
              );
            })()}
          </>
        ) : (
          // Show original total and previous data when no merchants
          <>
            <Flex alignItems="center" mb="1">
              <Box w="10px" h="10px" bg="#000000" borderRadius="50%" mr="2" />
              <Text fontSize="sm">
                {data.time}: {data.total}
              </Text>
            </Flex>
            <Flex alignItems="center" mb="1">
              <Box w="10px" h="10px" bg="#D3D3D3" borderRadius="50%" mr="2" />
              <Text fontSize="sm">
                {data.prevTime}: {data.prevTotal}
              </Text>
            </Flex>
          </>
        )}
      </div>
    );
  }
  return null;
};

CustomTooltipForPowerbankRented.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  powerbankShowAvg: PropTypes.bool,
  grossRevenueCountAvg: PropTypes.number,
  globalMerchantColors: PropTypes.object,
};

export const CustomTooltipForRecurring = ({
  active,
  payload,
  adminDetails,
  sideBarRoutes,
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const label =
      adminDetails?.role !== sideBarRoutes?.Admin ? (
        <p>Station: {data.name}</p>
      ) : (
        <p>Merchant: {data.name}</p>
      );
    return (
      <div
        className="custom-tooltip"
        style={{
          background: '#fff',
          padding: '10px',
          border: '1px solid #ccc',
        }}
      >
        {label}
        <p>Count: {data.count}</p>
      </div>
    );
  }
  return null;
};

CustomTooltipForRecurring.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  adminDetails: PropTypes.object,
  sideBarRoutes: PropTypes.object,
};

/**
 * RAT-1926: shared body for the Gross Revenue Per Station detail. Rendered
 * inside a floating recharts tooltip on desktop and inside a bottom drawer on
 * touch, where there is no hover to trigger a tooltip with.
 */
export const StationRevenueDetail = ({
  title,
  revenue,
  count,
  stations,
  colorAt,
  currency,
  maxRows = 8,
  listMaxHeight = '200px',
  moreHint,
}) => {
  const visibleStations = stations?.slice(0, maxRows) ?? [];
  const remainingCount = (stations?.length ?? 0) - visibleStations.length;

  return (
    <Box>
      <Text
        fontWeight="bold"
        fontSize={{ base: 'md', md: 'lg' }}
        mb="2"
        noOfLines={1}
        title={title}
      >
        {title}
      </Text>

      <Flex gap="4" mb={stations?.length ? '3' : '0'}>
        <Box>
          <Text fontSize="xs" color="gray.500">
            Total revenue
          </Text>
          <Text fontWeight="bold" fontSize="sm">
            {formatCurrency(revenue, currency)}
          </Text>
        </Box>
        <Box borderLeft="1px solid" borderColor="gray.200" pl="4">
          <Text fontSize="xs" color="gray.500">
            Total count
          </Text>
          <Text fontWeight="bold" fontSize="sm">
            {count}
          </Text>
        </Box>
      </Flex>

      {stations?.length > 0 && (
        <Box borderTop="1px solid" borderColor="gray.200" pt="2">
          <Text fontSize="xs" color="gray.500" mb="2">
            Revenue by station
          </Text>
          <Flex fontSize="11px" color="gray.500" mb="1">
            <Box flex="1">Station ID</Box>
            <Box w="80px" textAlign="right">
              Revenue
            </Box>
            <Box w="45px" textAlign="right">
              Count
            </Box>
          </Flex>
          <Box maxHeight={listMaxHeight} overflowY="auto">
            {visibleStations.map((station, index) => (
              <Flex key={station.stationId} fontSize="xs" mb="1" align="center">
                <Flex flex="1" align="center" gap="2" minW="0">
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={colorAt(index)}
                    flexShrink="0"
                  />
                  <Text isTruncated>{station.stationId}</Text>
                </Flex>
                <Box w="80px" textAlign="right">
                  {formatCurrency(station.revenue, currency)}
                </Box>
                <Box w="45px" textAlign="right">
                  {station.count}
                </Box>
              </Flex>
            ))}
          </Box>
          {remainingCount > 0 && (
            <Text fontSize="11px" color="gray.500" mt="1">
              +{remainingCount} more{moreHint ? ` - ${moreHint}` : ''}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

StationRevenueDetail.propTypes = {
  title: PropTypes.string,
  revenue: PropTypes.number,
  count: PropTypes.number,
  stations: PropTypes.array,
  colorAt: PropTypes.func.isRequired,
  currency: PropTypes.string,
  maxRows: PropTypes.number,
  listMaxHeight: PropTypes.string,
  moreHint: PropTypes.string,
};

export const CustomTooltipForStationRevenue = ({
  active,
  payload,
  colorAt,
  currency,
}) => {
  if (!active || !payload?.length) return null;

  const row = payload[0].payload;

  return (
    <Box
      bg="white"
      p="3"
      border="1px solid #ddd"
      borderRadius="12px"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.12)"
      maxWidth="320px"
    >
      <StationRevenueDetail
        title={row.name}
        revenue={row.revenue}
        count={row.count}
        stations={row.stations}
        colorAt={colorAt}
        currency={currency}
        maxRows={5}
        listMaxHeight="110px"
        moreHint="click the bar for the full list"
      />
    </Box>
  );
};

CustomTooltipForStationRevenue.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  colorAt: PropTypes.func.isRequired,
  currency: PropTypes.string,
};
